import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

const ROLE_OPTIONS = ["Admin", "Supervisor", "Guard", "Manager"];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "disapproved", label: "Disapproved" },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  designation: "",
  role: "",
  status: "pending",
  username: "",
  password: "",
  guard_seq: "",
  memberId: "",
};

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function AddUser() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const isEdit = params.get("mode") === "edit";
  const memberIdFromQuery = params.get("member_id") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [form, setForm] = useState(initialForm);
  const [guards, setGuards] = useState([]);
  const [staffDbConnected, setStaffDbConnected] = useState(true);
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState({});
  const [selectedSubmodules, setSelectedSubmodules] = useState({});
  const [licensedUsers, setLicensedUsers] = useState(0);
  const [currentUsers, setCurrentUsers] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const normalizeGuards = (payload) => {
    const list =
      payload?.guards ||
      payload?.staff ||
      payload?.payrollStaff ||
      payload?.data?.guards ||
      [];

    return Array.isArray(list) ? list : [];
  };

  const fetchPageData = async (local, token) => {
    try {
      const [dashboardRes, formRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(
          `${API_BASE_URL}/users/form-init${
            isEdit ? `?mode=edit&member_id=${encodeURIComponent(memberIdFromQuery)}` : ""
          }`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!formRes.ok) throw new Error("Failed to fetch user form data");

      const dashboardResult = await dashboardRes.json();
      const formResult = await formRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const payload = formResult?.data || {};

      const guardList = normalizeGuards(payload);
      console.log("Payroll guards from /users/form-init:", guardList);

      setLicensedUsers(Number(payload.licensedUsers || 0));
      setCurrentUsers(Number(payload.currentUsers || 0));
      setLimitReached(!!payload.limitReached);
      setGuards(guardList);
      setStaffDbConnected(payload.staffDbConnected !== false);

      const modulesWithSubs = (payload.modules || []).map((m) => ({
        ...m,
        page_id: String(m.page_id),
        submodules: payload.subpagesByPage?.[String(m.page_id)] || m.submodules || [],
      }));

      setModules(modulesWithSubs);

      const vm = payload.vm || {};

      setForm({
        ...initialForm,
        fullName: vm.fullName || "",
        email: vm.email || "",
        phone: vm.phone || "",
        designation: vm.designation || "",
        role: vm.role || "",
        status: vm.status || "pending",
        username: vm.username || "",
        password: "",
        guard_seq: vm.guard_seq === null || vm.guard_seq === undefined ? "" : String(vm.guard_seq),
        memberId: vm.memberId || "",
      });

      const existingPermissions = payload.existingPermissions || {};
      const modulesState = {};
      const submodulesState = {};

      modulesWithSubs.forEach((module) => {
        const pageId = String(module.page_id);
        const permission = existingPermissions?.[pageId];
        const enabled = !!permission?.main_access;

        modulesState[pageId] = enabled;
        submodulesState[pageId] = enabled ? Object.keys(permission?.submodules || {}) : [];
      });

      setSelectedModules(modulesState);
      setSelectedSubmodules(submodulesState);
    } catch (error) {
      console.error("Add user fetch error:", error);
      setErrorMessage(error.message || "Failed to load page data.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const remainingUsers = useMemo(() => {
    if (licensedUsers > 0) return Math.max(0, licensedUsers - currentUsers);
    return null;
  }, [licensedUsers, currentUsers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleModule = (pageId) => {
    const key = String(pageId);

    setSelectedModules((prev) => {
      const next = !prev[key];

      if (!next) {
        setSelectedSubmodules((old) => ({ ...old, [key]: [] }));
      }

      return { ...prev, [key]: next };
    });
  };

  const toggleSubmodule = (pageId, submoduleName) => {
    const key = String(pageId);
    if (!selectedModules[key]) return;

    setSelectedSubmodules((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(submoduleName);

      return {
        ...prev,
        [key]: exists
          ? current.filter((item) => item !== submoduleName)
          : [...current, submoduleName],
      };
    });
  };

  const buildPayload = () => {
    const perm_module = {};
    const perm_sub = {};

    Object.entries(selectedModules).forEach(([pageId, enabled]) => {
      if (enabled) {
        perm_module[pageId] = true;
        perm_sub[pageId] = selectedSubmodules[pageId] || [];
      }
    });

    return {
      form_mode: isEdit ? "edit" : "create",
      fullName: form.fullName,
      email: form.email,
      phone: form.phone,
      designation: form.designation,
      role: form.role,
      status: form.status,
      username: form.username,
      password: form.password,
      guard_seq: form.guard_seq === "" ? null : Number(form.guard_seq),
      memberId: form.memberId,
      perm_module,
      perm_sub,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.fullName.trim() || !form.username.trim() || !form.role.trim()) {
      setErrorMessage("Please fill all required fields.");
      return;
    }

    if (!isEdit && limitReached) {
      setErrorMessage(`User limit reached (${currentUsers} of ${licensedUsers}). You cannot add more users.`);
      return;
    }

    try {
      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/users/${isEdit ? "update" : "create"}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildPayload()),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save user");
      }

      setSuccessMessage(isEdit ? "User updated successfully!" : "User created successfully!");

      setTimeout(() => {
        navigate("/user_list");
      }, 1000);
    } catch (error) {
      console.error("Save user error:", error);
      setErrorMessage(error.message || "Failed to save user.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading add user page...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="add_user"
      activeMenu="users"
    >
      <PageHeader isEdit={isEdit} onBack={() => navigate("/user_list")} />

      <LicenseBanner
        licensedUsers={licensedUsers}
        currentUsers={currentUsers}
        remainingUsers={remainingUsers}
        limitReached={limitReached}
      />

      {successMessage && <Alert type="success" message={successMessage} />}
      {errorMessage && <Alert type="error" message={errorMessage} />}

      <form onSubmit={handleSubmit}>
        <FormSection
          title="Basic Information"
          subtitle={isEdit ? "Modify user profile details" : "Enter the user's profile details"}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Field label="Full Name *">
              <Input name="fullName" value={form.fullName} onChange={handleChange} required />
            </Field>

            <Field label="Email">
              <Input type="email" name="email" value={form.email} onChange={handleChange} />
            </Field>

            <Field label="Phone">
              <Input name="phone" value={form.phone} onChange={handleChange} />
            </Field>

            <Field label="Designation">
              <Input name="designation" value={form.designation} onChange={handleChange} />
            </Field>

            <Field label="Role *">
              <Select name="role" value={form.role} onChange={handleChange} required>
                <option value="">Select role</option>
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Status">
              <Select name="status" value={form.status} onChange={handleChange}>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </FormSection>

        <FormSection title="Login Credentials" subtitle="Set username, password, and payroll staff mapping">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Field label="Username *">
              <Input name="username" value={form.username} onChange={handleChange} required />
            </Field>

            <Field label={isEdit ? "Password (leave blank to keep current)" : "Password *"}>
              <Input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required={!isEdit}
              />
            </Field>

          <Field label="Payroll Staff / Guard">
  <Select name="guard_seq" value={form.guard_seq} onChange={handleChange}>
    <option value="">-- Select Payroll Staff --</option>

    {!staffDbConnected ? (
      <option value="" disabled>
        Payroll DB connection failed
      </option>
    ) : guards.length === 0 ? (
      <option value="" disabled>
        No payroll staff found
      </option>
    ) : (
      guards.map((g, index) => {
        const seq =
          g.staff_seq ??
          g.staffSeq ??
          g.seq ??
          g.id ??
          g.staff_id ??
          "";

        const name =
          g.staff_name ||
          g.staffName ||
          g.name ||
          g.employee_name ||
          g.full_name ||
          "";

        const applicationNumber =
          g.application_number ||
          g.applicationNumber ||
          g.app_no ||
          g.appNo ||
          "";

        const label = `${name}${
          applicationNumber ? ` (${applicationNumber})` : ""
        }`;

        return (
          <option key={seq || index} value={String(seq)}>
            {label || `Staff ${index + 1}`}
          </option>
        );
      })
    )}
  </Select>
</Field>

            <Field label="Member ID">
              <Input
                name="memberId"
                value={form.memberId}
                readOnly
                className="bg-slate-50 text-slate-500"
              />
            </Field>
          </div>
        </FormSection>

        <FormSection title="Module Access & Permissions" subtitle="Toggle modules and choose submodule access">
          <div className="grid gap-5">
            {modules.length === 0 ? (
              <EmptyState>No modules found.</EmptyState>
            ) : (
              modules.map((module) => (
                <ModuleCard
                  key={module.page_id}
                  module={module}
                  enabled={!!selectedModules[String(module.page_id)]}
                  selectedSubs={selectedSubmodules[String(module.page_id)] || []}
                  onToggleModule={toggleModule}
                  onToggleSubmodule={toggleSubmodule}
                />
              ))
            )}
          </div>
        </FormSection>

        <div className="flex justify-end gap-3 flex-wrap mt-2 mb-5">
          <button
            type="button"
            onClick={() => navigate("/user_list")}
            className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Back to User List
          </button>

          <button
            type="submit"
            disabled={saving || (!isEdit && limitReached)}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed bg-gradient-to-br from-slate-800 to-blue-600"
            style={{ fontFamily: FONT_UI }}
          >
            {saving ? "Saving..." : isEdit ? "Update User" : "+ Create User"}
          </button>
        </div>
      </form>
    </Layout>
  );
}

function PageHeader({ isEdit, onBack }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          User Management
        </h2>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {isEdit ? "Update existing user details and permissions" : "Create a new user and assign permissions"}
        </p>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3 className="m-0 text-[22px] font-normal text-slate-900" style={{ fontFamily: FONT_HEADING }}>
            {isEdit ? "Edit User" : "Add New User"}
          </h3>
          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Fill the form below to {isEdit ? "update the account" : "create the account"}
          </p>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20"
          style={{
            background: skin.gradient,
            fontFamily: FONT_UI,
          }}
        >
          Back to User List
        </button>
      </div>
    </>
  );
}

function LicenseBanner({ licensedUsers, currentUsers, remainingUsers, limitReached }) {
  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        limitReached
          ? "bg-red-50 border-red-200 text-red-700"
          : "bg-green-50 border-green-200 text-green-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      <strong>User Capacity:</strong>{" "}
      {licensedUsers > 0 ? `${currentUsers} / ${licensedUsers}` : `${currentUsers} (no limit set)`}
      {" · "}
      {limitReached
        ? "Limit reached — new user creation is disabled."
        : licensedUsers > 0
        ? `You can add ${remainingUsers} user(s).`
        : "You can add more user(s)."}
    </div>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function FormSection({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
      <div className="mb-5">
        <h3 className="m-0 text-[22px] font-normal text-slate-900" style={{ fontFamily: FONT_HEADING }}>
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {subtitle}
        </p>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block mb-2 text-sm font-semibold text-slate-900" style={{ fontFamily: FONT_UI }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </select>
  );
}

function EmptyState({ children }) {
  return (
    <div
      className="p-4 rounded-xl border border-dashed border-slate-300 text-slate-500 text-sm bg-slate-50"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </div>
  );
}

function ModuleCard({ module, enabled, selectedSubs, onToggleModule, onToggleSubmodule }) {
  const skin = useSkin();
  const subs = module.submodules || [];

  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
      <div
        className="text-white px-5 py-4 flex justify-between items-center gap-3 flex-wrap"
        style={{ background: skin.gradient }}
      >
        <div className="text-[17px] font-semibold" style={{ fontFamily: FONT_UI }}>
          {module.modules}
        </div>

        <label
          className="flex items-center gap-2.5 text-sm font-semibold cursor-pointer"
          style={{ fontFamily: FONT_UI }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={() => onToggleModule(module.page_id)}
            className="h-4 w-4"
          />
          Enable Module
        </label>
      </div>

      <div className="p-5">
        {subs.length === 0 ? (
          <EmptyState>No sub-modules. Enabling grants full access.</EmptyState>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {subs.map((sub) => {
              const checked = selectedSubs.includes(sub.submodules);

              return (
                <label
                  key={`${module.page_id}-${sub.submodules}`}
                  className={`flex items-start gap-3 border border-slate-200 rounded-xl p-3 ${
                    enabled
                      ? "bg-white cursor-pointer"
                      : "bg-slate-50 opacity-60 cursor-not-allowed"
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={!enabled}
                    checked={checked}
                    onChange={() => onToggleSubmodule(module.page_id, sub.submodules)}
                    className="mt-1 h-4 w-4"
                  />

                  <div>
                    <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: FONT_UI }}>
                      {sub.submodules}
                    </div>

                    {sub.links ? (
                      <div className="mt-1 text-xs text-slate-500" style={{ fontFamily: FONT_UI }}>
                        {sub.links}
                      </div>
                    ) : null}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}